import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    InputBase,
    alpha,
    MenuItem,
    Select,
    FormControl,
    Avatar,
    Chip,
    Pagination,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    GridView as GridIcon,
    ViewList as ListIcon,
    MoreVert as MoreIcon,
    ManageAccounts as ManageIcon,
    Business as BusinessIcon,
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
    People as PeopleIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { OrgMemberAdd, OrgMemberResponse, OrganisationResponse } from 'src/api/Api';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import {
    ORGANISATION_AVATAR_COLOR,
    formatStatusLabel,
    getOrganisationInitials,
    getStatusColor,
} from './organisationUtils';

const rowsPerPage = 9;

interface ApiErrorShape {
    detail?: { msg?: string }[];
    error?: { message?: string };
    message?: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    const apiError = error as ApiErrorShape;
    return apiError?.detail?.[0]?.msg || apiError?.error?.message || apiError?.message || fallback;
};

const getMemberName = (member: OrgMemberResponse): string => {
    const fullName = `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim();
    return fullName || member.user?.email || member.user_id;
};

const getMemberInitial = (member: OrgMemberResponse): string =>
    (member.user?.first_name?.[0] || member.user?.email?.[0] || member.user_id[0] || 'U').toUpperCase();

const AddOrganisationMemberDialog: React.FC<{
    open: boolean;
    orgId: string;
    onClose: () => void;
    onMemberAdded: () => void;
}> = ({ open, orgId, onClose, onMemberAdded }) => {
    const [formData, setFormData] = useState<OrgMemberAdd>({
        user_id: '',
        role: 'fieldworker',
    });
    const [loading, setLoading] = useState(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    const resetAndClose = () => {
        if (loading) {
            return;
        }
        setFormData({ user_id: '', role: 'fieldworker' });
        onClose();
    };

    const handleCreate = async () => {
        if (!formData.user_id.trim() || !formData.role.trim()) {
            showSnackbar({ message: 'User ID and role are required.', type: snackbarType.WARNING });
            return;
        }

        setLoading(true);
        try {
            await clientService.api.addOrganisationMemberApiV1OrganisationsOrgIdMembersPost(orgId, {
                user_id: formData.user_id.trim(),
                role: formData.role.trim(),
            });
            showSnackbar({ message: 'Member added successfully.', type: snackbarType.SUCCESS });
            await onMemberAdded();
            setFormData({ user_id: '', role: 'fieldworker' });
            onClose();
        } catch (error) {
            showSnackbar({
                message: getErrorMessage(error, 'Failed to add member.'),
                type: snackbarType.ERROR,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={resetAndClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#0D0D0D',
                    backgroundImage: 'none',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    p: 2,
                },
            }}
        >
            <DialogTitle sx={{ color: '#fff', fontWeight: 800, fontSize: '1.5rem', pb: 1 }}>
                Add Member
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1, fontSize: '0.9rem' }}>
                        Add an existing user to this organisation.
                    </Typography>

                    <Box>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1, ml: 0.5 }}>
                            User ID
                        </Typography>
                        <TextField
                            value={formData.user_id}
                            onChange={(event) => setFormData(prev => ({ ...prev, user_id: event.target.value }))}
                            fullWidth
                            required
                            autoComplete="off"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', mb: 1, ml: 0.5 }}>
                            Role
                        </Typography>
                        <TextField
                            value={formData.role}
                            onChange={(event) => setFormData(prev => ({ ...prev, role: event.target.value }))}
                            fullWidth
                            required
                            autoComplete="off"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={resetAndClose}
                    disabled={loading}
                    sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'none', fontWeight: 600 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={loading || !formData.user_id.trim() || !formData.role.trim()}
                    sx={{
                        bgcolor: '#10B981',
                        '&:hover': { bgcolor: '#059669' },
                        color: '#000',
                        fontWeight: 800,
                        textTransform: 'none',
                        px: 4,
                        borderRadius: '10px',
                        minWidth: '120px',
                    }}
                >
                    {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Add Member'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Member Card (mirrors Users.tsx visual treatment) ─────────────────────────
const MemberCard: React.FC<{
    member: OrgMemberResponse;
    organisationName: string;
    onManage: (member: OrgMemberResponse) => void;
    onDelete: (id: string) => void;
}> = ({ member, organisationName, onManage, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const statusColor = getStatusColor(member.status);
    const email = member.user?.email || member.user_id;

    return (
        <Box sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            p: 3,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                transform: 'translateY(-6px)',
                borderColor: alpha('#10B981', 0.2),
                boxShadow: `0 12px 24px -8px ${alpha('#000', 0.5)}`
            }
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, minHeight: '64px', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 2, flex: 1, minWidth: 0 }}>
                    <Avatar sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '14px',
                        bgcolor: alpha('#10B981', 0.1),
                        color: '#10B981',
                        fontWeight: 800,
                        fontSize: '1.2rem',
                        border: `1px solid ${alpha('#10B981', 0.2)}`
                    }}>
                        {getMemberInitial(member)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '1.05rem',
                                lineHeight: 1.2,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}>
                                {getMemberName(member)}
                            </Typography>
                            <Chip
                                label={member.role.toUpperCase()}
                                size="small"
                                sx={{
                                    height: '18px',
                                    fontSize: '0.6rem',
                                    fontWeight: 900,
                                    bgcolor: alpha('#10B981', 0.1),
                                    color: '#34D399',
                                    border: '1px solid currentColor',
                                    letterSpacing: '0.05em'
                                }}
                            />
                        </Box>
                        <Typography noWrap sx={{ color: 'rgba(255, 255, 255, 0.35)', fontSize: '0.8rem', fontWeight: 500 }}>
                            {email}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, pl: 1 }}>
                    <Box sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: statusColor,
                        boxShadow: `0 0 10px ${statusColor}`
                    }} />
                    <Typography sx={{ color: statusColor, fontSize: '0.7rem', fontWeight: 700, textTransform: 'capitalize' }}>
                        {formatStatusLabel(member.status)}
                    </Typography>
                </Box>
            </Box>

            {/* Middle */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>
                        Organisation
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <BusinessIcon sx={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.2)' }} />
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem', fontWeight: 500 }}>
                            {organisationName}
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                            Organisation Role
                        </Typography>
                        <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.2)', p: 0.5, '&:hover': { color: '#10B981' } }}>
                            <EditIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.5 }}>
                        <Typography sx={{ color: '#10B981', fontWeight: 800, fontSize: '1.1rem', textTransform: 'capitalize' }}>{member.role}</Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: '5px', bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '10px', overflow: 'hidden' }}>
                        <Box sx={{ width: member.status?.toLowerCase() === 'active' ? '100%' : '35%', height: '100%', bgcolor: '#10B981' }} />
                    </Box>
                </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ pt: 3, mt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', gap: 1.5 }}>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ManageIcon sx={{ fontSize: '1.1rem' }} />}
                    onClick={() => onManage(member)}
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1.2,
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        '&:hover': {
                            bgcolor: alpha('#10B981', 0.1),
                            borderColor: alpha('#10B981', 0.3),
                            color: '#10B981'
                        }
                    }}
                >
                    MANAGE USER
                </Button>
                <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        color: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        p: 1.2,
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)', color: '#fff' }
                    }}
                >
                    <MoreIcon sx={{ fontSize: '1.4rem' }} />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        sx: {
                            bgcolor: '#1A1A1A',
                            color: '#fff',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
                            mt: 1,
                            '& .MuiMenuItem-root': {
                                fontSize: '0.9rem',
                                py: 1.2,
                                px: 2,
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                            }
                        }
                    }}
                >
                    <MenuItem onClick={() => { setAnchorEl(null); onDelete(member.org_member_id); }} sx={{ color: '#F87171', fontWeight: 600 }}>
                        Delete Member
                    </MenuItem>
                </Menu>
            </Box>
        </Box>
    );
};

// ─── Member List Row ─────────────────────────────────────────────────────────
const MemberListRow: React.FC<{
    member: OrgMemberResponse;
    organisationName: string;
    onManage: (member: OrgMemberResponse) => void;
    onDelete: (id: string) => void;
}> = ({ member, onManage, onDelete }) => {
    const statusColor = getStatusColor(member.status);
    const firstName = member.user?.first_name || '—';
    const lastName = member.user?.last_name || '—';
    const email = member.user?.email || member.user_id;

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '52px 1fr 1fr 1.5fr 120px 100px 160px',
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: 2,
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.04)',
            borderRadius: '14px',
            transition: 'all 0.2s ease',
            '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(16, 185, 129, 0.15)',
            },
        }}>
            {/* Avatar */}
            <Avatar sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: alpha('#10B981', 0.1),
                color: '#10B981',
                fontWeight: 800,
                fontSize: '0.95rem',
                border: `1px solid ${alpha('#10B981', 0.2)}`,
            }}>
                {getMemberInitial(member)}
            </Avatar>

            {/* First Name */}
            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {firstName}
            </Typography>

            {/* Last Name */}
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lastName}
            </Typography>

            {/* Email */}
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {email}
            </Typography>

            {/* Role */}
            <Chip
                label={member.role.toUpperCase()}
                size="small"
                sx={{
                    height: '20px',
                    fontSize: '0.6rem',
                    fontWeight: 900,
                    bgcolor: alpha('#10B981', 0.1),
                    color: '#34D399',
                    border: '1px solid currentColor',
                    letterSpacing: '0.05em',
                    justifySelf: 'start',
                }}
            />

            {/* Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor, boxShadow: `0 0 8px ${statusColor}`, flexShrink: 0 }} />
                <Typography sx={{ color: statusColor, fontSize: '0.8rem', fontWeight: 700, textTransform: 'capitalize' }}>
                    {formatStatusLabel(member.status)}
                </Typography>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ManageIcon sx={{ fontSize: '0.9rem' }} />}
                    onClick={() => onManage(member)}
                    sx={{
                        color: 'rgba(255,255,255,0.6)',
                        borderColor: 'rgba(255,255,255,0.1)',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        borderRadius: '8px',
                        '&:hover': { borderColor: '#10B981', color: '#10B981', bgcolor: alpha('#10B981', 0.05) },
                    }}
                >
                    Manage
                </Button>
                <IconButton
                    size="small"
                    onClick={() => onDelete(member.org_member_id)}
                    sx={{ color: 'rgba(248,113,113,0.5)', '&:hover': { color: '#F87171', bgcolor: alpha('#F87171', 0.08) }, borderRadius: '8px' }}
                >
                    <EditIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
            </Box>
        </Box>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const OrgMembersPage: React.FC = () => {
    const { orgId } = useParams<{ orgId: string }>();
    const navigate = useNavigate();

    const [organisation, setOrganisation] = useState<OrganisationResponse | null>(null);
    const [members, setMembers] = useState<OrgMemberResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
    const [memberToDelete, setMemberToDelete] = useState<string | null>(null);
    const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [page, setPage] = useState(1);

    const { showSnackbar, snackbarType } = useSnackbar();

    const fetchOrganisation = useCallback(async () => {
        if (!orgId) {
            return;
        }

        try {
            const response = await clientService.api.getOrganisationApiV1OrganisationsOrgIdGet(orgId);
            setOrganisation(response.data);
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Failed to fetch organisation.');
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        }
    }, [orgId, showSnackbar, snackbarType.ERROR]);

    const fetchMembers = useCallback(async () => {
        if (!orgId) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await clientService.api.listOrganisationMembersApiV1OrganisationsOrgIdMembersGet(orgId, {
                skip: 0,
                limit: 500,
            });
            setMembers(response.data.data || []);
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Failed to fetch organisation members.');
            setError(errorMessage);
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [orgId, showSnackbar, snackbarType.ERROR]);

    useEffect(() => {
        fetchOrganisation();
        fetchMembers();
    }, [fetchOrganisation, fetchMembers]);

    const roleOptions = useMemo(() => Array.from(new Set(members.map(member => member.role).filter(Boolean))), [members]);
    const statusOptions = useMemo(() => Array.from(new Set(members.map(member => member.status).filter(Boolean))), [members]);

    const filteredMembers = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();

        return members.filter(member => {
            const matchesQuery = !query || [
                getMemberName(member),
                member.user?.email || '',
                member.user_id,
                member.role,
                member.status,
            ].join(' ').toLowerCase().includes(query);
            const matchesRole = roleFilter === 'all' || member.role === roleFilter;
            const matchesStatus = statusFilter === 'all' || member.status === statusFilter;

            return matchesQuery && matchesRole && matchesStatus;
        });
    }, [members, roleFilter, searchQuery, statusFilter]);

    useEffect(() => {
        setPage(1);
    }, [roleFilter, searchQuery, statusFilter]);

    const paginatedMembers = filteredMembers.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    const totalPages = Math.max(1, Math.ceil(filteredMembers.length / rowsPerPage));
    const organisationName = organisation?.name || 'Organisation';

    const handleDeleteClick = (memberId: string) => {
        setMemberToDelete(memberId);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!memberToDelete) {
            return;
        }

        try {
            await clientService.api.deleteOrgMemberApiV1OrgMembersOrgMemberIdDelete(memberToDelete);
            showSnackbar({ message: 'Member deleted successfully!', type: snackbarType.SUCCESS });
            await fetchMembers();
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Failed to delete member.');
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setOpenDeleteDialog(false);
            setMemberToDelete(null);
        }
    };

    const handleManageMember = (member: OrgMemberResponse) => {
        navigate(`/admin/user/${member.user_id}/update`, { state: { orgId } });
    };

    return (
        <Box sx={{ px: 4, py: 4, bgcolor: '#0D0D0D', minHeight: '100%', borderRadius: '16px' }}>

            {/* ── Org Header ── */}
            <Box sx={{ mb: 5 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(`/admin/organisation/${orgId}`)}
                    sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600, mb: 2, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}
                >
                    Back to {organisationName}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                        {organisation && (
                            <Avatar sx={{
                                width: 52,
                                height: 52,
                                borderRadius: '14px',
                                bgcolor: alpha(ORGANISATION_AVATAR_COLOR, 0.15),
                                color: ORGANISATION_AVATAR_COLOR,
                                fontWeight: 800,
                                fontSize: '1.4rem',
                                border: `1px solid ${alpha(ORGANISATION_AVATAR_COLOR, 0.35)}`,
                            }}>
                                {getOrganisationInitials(organisation.name)}
                            </Avatar>
                        )}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800 }}>
                                    Members
                                </Typography>
                                {organisation && (
                                    <Chip
                                        label={organisation.name}
                                        size="small"
                                        sx={{
                                            bgcolor: alpha(ORGANISATION_AVATAR_COLOR, 0.12),
                                            color: ORGANISATION_AVATAR_COLOR,
                                            border: `1px solid ${alpha(ORGANISATION_AVATAR_COLOR, 0.3)}`,
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                Manage members, roles, and project access for this organisation.
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: 'rgba(59,130,246,0.08)',
                            border: '1px solid rgba(59,130,246,0.2)',
                            borderRadius: '10px',
                            px: 2,
                            py: 1,
                        }}>
                            <PeopleIcon sx={{ fontSize: '1.1rem', color: '#3B82F6' }} />
                            <Typography sx={{ color: '#3B82F6', fontWeight: 700, fontSize: '0.9rem' }}>
                                {members.length} Members
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCreateDialog(true)}
                            sx={{
                                bgcolor: '#10B981',
                                '&:hover': { bgcolor: '#059669' },
                                color: '#000',
                                fontWeight: 800,
                                textTransform: 'none',
                                px: 3,
                                py: 1,
                                borderRadius: '10px'
                            }}
                        >
                            Add Member
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ── Filters Bar ── */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    px: 2,
                    height: '48px',
                    maxWidth: '400px',
                    '&:focus-within': { borderColor: 'rgba(16,185,129,0.4)' },
                }}>
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.3)', mr: 1.5 }} />
                    <InputBase
                        placeholder="Search members by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ color: '#fff', width: '100%', fontSize: '0.9rem' }}
                    />
                </Box>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={roleFilter}
                        onChange={(event) => setRoleFilter(event.target.value)}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.03)',
                            color: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '10px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.05)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                        }}
                    >
                        <MenuItem value="all">Role: All</MenuItem>
                        {roleOptions.map(role => (
                            <MenuItem key={role} value={role}>{formatStatusLabel(role)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.03)',
                            color: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '10px',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.05)' },
                        }}
                    >
                        <MenuItem value="all">Status: All</MenuItem>
                        {statusOptions.map(status => (
                            <MenuItem key={status} value={status}>{formatStatusLabel(status)}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 2,
                        height: '40px'
                    }}
                >
                    Filters
                </Button>

                <Box sx={{ display: 'flex', bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: '10px', p: 0.5 }}>
                    <IconButton
                        size="small"
                        onClick={() => setViewMode('grid')}
                        sx={{ color: viewMode === 'grid' ? '#10B981' : 'rgba(255, 255, 255, 0.3)', bgcolor: viewMode === 'grid' ? 'rgba(16, 185, 129, 0.1)' : 'transparent' }}
                    >
                        <GridIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setViewMode('list')}
                        sx={{ color: viewMode === 'list' ? '#10B981' : 'rgba(255, 255, 255, 0.3)', bgcolor: viewMode === 'list' ? 'rgba(16, 185, 129, 0.1)' : 'transparent' }}
                    >
                        <ListIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                </Box>
            </Box>

            {/* ── Content Area ── */}
            {loading && members.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress sx={{ color: '#10B981' }} />
                </Box>
            ) : error ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography sx={{ color: '#F87171', mb: 2 }}>{error}</Typography>
                    <Button variant="outlined" onClick={fetchMembers}>Retry</Button>
                </Box>
            ) : filteredMembers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '16px' }}>
                    <PeopleIcon sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.15)', mb: 2 }} />
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', mb: 1 }}>No members found for this organisation.</Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.25)', fontSize: '0.85rem' }}>
                        Add members to get started.
                    </Typography>
                </Box>
            ) : viewMode === 'list' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Table Header */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '52px 1fr 1fr 1.5fr 120px 100px 160px',
                        gap: 2,
                        px: 3,
                        pb: 1.5,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        mb: 0.5,
                    }}>
                        {['', 'First Name', 'Last Name', 'Email', 'Role', 'Status', ''].map((label, i) => (
                            <Typography key={i} sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                {label}
                            </Typography>
                        ))}
                    </Box>
                    {/* Rows */}
                    {paginatedMembers.map((member) => (
                        <MemberListRow
                            key={member.org_member_id}
                            member={member}
                            organisationName={organisationName}
                            onManage={handleManageMember}
                            onDelete={handleDeleteClick}
                        />
                    ))}
                </Box>
            ) : (
                <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
                    {paginatedMembers.map((member) => (
                        <Grid key={member.org_member_id} size={{ xs: 12, sm: 6, lg: 4 }} sx={{ display: 'flex' }}>
                            <MemberCard
                                member={member}
                                organisationName={organisationName}
                                onManage={handleManageMember}
                                onDelete={handleDeleteClick}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* ── Pagination ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 6 }}>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.85rem' }}>
                    Showing {paginatedMembers.length === 0 ? 0 : ((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, filteredMembers.length)} of {filteredMembers.length} members
                </Typography>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            color: 'rgba(255, 255, 255, 0.5)',
                            bgcolor: 'transparent',
                            borderRadius: '8px',
                            '&.Mui-selected': {
                                bgcolor: alpha('#10B981', 0.1),
                                color: '#10B981',
                                border: '1px solid currentColor'
                            },
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                        }
                    }}
                />
            </Box>

            <ConfirmationDialog
                open={openDeleteDialog}
                title="Confirm Deletion"
                content="Are you sure you want to remove this member? This action is irreversible and will permanently remove all associated project access."
                onConfirm={handleConfirmDelete}
                onCancel={() => setOpenDeleteDialog(false)}
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
            />

            {orgId && (
                <AddOrganisationMemberDialog
                    open={openCreateDialog}
                    orgId={orgId}
                    onClose={() => setOpenCreateDialog(false)}
                    onMemberAdded={fetchMembers}
                />
            )}
        </Box>
    );
};

export default OrgMembersPage;
